/**
 * A card — the unit of composition for every screen.
 *
 * White on the off-white page ground, separated by elevation rather than by a
 * border. That is the reference prototype's choice and it is load-bearing: the
 * page background is `--gv-off-white`, so a bordered white box on it reads as a
 * form field, while a shadowed one reads as a surface. Phase 6 built these
 * bordered and the difference is most of why the interface looked unlike the
 * mockup (§4.5).
 *
 * `CardHead` and `CardBody` are separate components rather than props because
 * a head is optional, may hold actions, and needs its own padding and rule.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.4, §4.5
 */
import { cn } from '../../lib/cn';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
}

/** The surface. */
export function Card({ children, className }: CardProps): React.JSX.Element {
  return <div className={cn('rounded-md bg-white shadow-sm', className)}>{children}</div>;
}

export interface CardHeadProps {
  children: React.ReactNode;
  /**
   * Actions pushed to the trailing edge — a button, a link, a toggle. Kept a
   * separate slot so the head's layout does not depend on child order.
   */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The card's header strip: a label on the left, actions on the right, a hairline
 * rule beneath.
 */
export function CardHead({
  children,
  actions,
  className,
}: CardHeadProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'border-gray-100 flex items-center gap-2.5 border-b px-s3 py-3.5',
        className,
      )}
    >
      {children}
      {actions !== undefined && (
        <div className="ml-auto flex items-center gap-s2">{actions}</div>
      )}
    </div>
  );
}

export interface CardBodyProps {
  children: React.ReactNode;
  /**
   * Remove the padding, for a body that is entirely a table or a list of rows
   * which supply their own. The mockup calls this `.card__body--flush`.
   */
  flush?: boolean;
  className?: string;
}

/** The card's content area. */
export function CardBody({
  children,
  flush = false,
  className,
}: CardBodyProps): React.JSX.Element {
  return <div className={cn(flush ? 'p-0' : 'p-s3', className)}>{children}</div>;
}
